import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { doc, getDoc } from 'firebase/firestore';
import { db, auth } from '@/lib/firebase/config';
import { Skeleton } from '@/components/ui/skeleton';
import { User } from 'firebase/auth';
import {
  LayoutDashboard,
  Store,
  LogOut,
  User as UserIcon,
  Loader2
} from 'lucide-react';

interface UserData {
  stripeAccountId?: string;
  displayName: string;
  email: string;
  createdAt: Date;
}

export default function Navigation() {
  const navigate = useNavigate();
  const [isSeller, setIsSeller] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  
  useEffect(() => {
    const fetchUserData = async (user: User) => {
      try {
        const userRef = doc(db, 'users', user.uid);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          const data = userSnap.data() as UserData;
          setUserData(data);
          setIsSeller(!!data.stripeAccountId);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    // Set up auth state listener if auth is properly configured
    if (auth) {
      const unsubscribe = auth.onAuthStateChanged((user) => {
        setUser(user);
        
        if (user) {
          fetchUserData(user);
        } else {
          setUserData(null);
          setIsSeller(false);
          setIsLoading(false);
        }
      });
      
      return () => unsubscribe();
    } else {
      // If auth is not configured, set loading to false
      setIsLoading(false);
    }
  }, []);

  const handleSignOut = async () => {
    try {
      if (auth) {
        await auth.signOut();
        navigate('/');
      }
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };
  
  return (
    <nav className="bg-background border-b sticky top-0 z-50">
      <div className="container mx-auto px-4 py-3 flex items-center justify-between">
        <div className="flex items-center">
          <Link to="/" className="text-xl font-bold flex items-center gap-2">
            <Store className="h-6 w-6" />
            AutomagicFlow
          </Link>
          
          <div className="ml-8 hidden md:flex items-center space-x-4">
            <Link 
              to="/marketplace" 
              className="text-muted-foreground hover:text-foreground flex items-center gap-2 px-3 py-2 rounded-md hover:bg-accent"
            >
              <Store className="h-4 w-4" />
              Marketplace
            </Link>
            
            {!isLoading && user && (
              <Link 
                to="/dashboard" 
                className="text-muted-foreground hover:text-foreground flex items-center gap-2 px-3 py-2 rounded-md hover:bg-accent"
              >
                <LayoutDashboard className="h-4 w-4" />
                My Workflows
              </Link>
            )}
            
            {!isLoading && isSeller && (
              <Link 
                to="/seller/dashboard" 
                className="text-muted-foreground hover:text-foreground flex items-center gap-2 px-3 py-2 rounded-md hover:bg-accent"
              >
                <Store className="h-4 w-4" />
                Seller Dashboard
              </Link>
            )}
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          {isLoading ? (
            <div className="flex items-center space-x-2">
              <Skeleton className="h-9 w-[100px]" />
              <Skeleton className="h-9 w-[100px]" />
            </div>
          ) : user ? (
            <>
              {!isSeller && (
                <Link to="/seller/onboarding">
                  <Button variant="outline" size="sm" className="flex items-center gap-2">
                    <Store className="h-4 w-4" />
                    Become a Seller
                  </Button>
                </Link>
              )}
              <Link to="/account">
                <Button variant="ghost" size="sm" className="flex items-center gap-2">
                  <UserIcon className="h-4 w-4" />
                  {userData?.displayName || 'My Account'}
                </Button>
              </Link>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={handleSignOut}
                className="flex items-center gap-2"
              >
                <LogOut className="h-4 w-4" />
                Sign Out
              </Button>
            </>
          ) : (
            <>
              <Link to="/auth?action=login">
                <Button variant="ghost" size="sm">Sign In</Button>
              </Link>
              <Link to="/auth?action=signup">
                <Button size="sm">Sign Up</Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </nav>
  );
} 